import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  circle,
  circleMarker,
  latLng,
  map as createMap,
  tileLayer,
  type Circle,
  type CircleMarker,
  type Layer,
  type LeafletMouseEvent,
  type Map as LeafletMap,
} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './LocationMapPicker.module.css';

const DEFAULT_CENTER: [number, number] = [40.4093, 49.8671];
const DEFAULT_ZOOM = 12;
const EMPTY_ZOOM = 6;

type LocationMapPickerProps = {
  latitude?: number;
  longitude?: number;
  radiusMeters?: number;
  disabled?: boolean;
  compact?: boolean;
  onChange: (latitude: number, longitude: number) => void;
};

const isCoord = (value?: number): value is number => typeof value === 'number' && Number.isFinite(value);

const hasMapSize = (map: LeafletMap): boolean => {
  const size = map.getSize();
  return size.x > 0 && size.y > 0;
};

const dropLayer = (map: LeafletMap | null, layer: Layer | null) => {
  if (!map || !layer || !map.hasLayer(layer)) {
    return;
  }
  map.removeLayer(layer);
};

export const LocationMapPicker: React.FC<LocationMapPickerProps> = ({
  latitude,
  longitude,
  radiusMeters,
  disabled = false,
  compact = false,
  onChange,
}) => {
  const { t } = useTranslation(['fieldOperations', 'companies', 'common']);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<CircleMarker | null>(null);
  const circleRef = useRef<Circle | null>(null);
  const onChangeRef = useRef(onChange);
  const disabledRef = useRef(disabled);
  onChangeRef.current = onChange;
  disabledRef.current = disabled;

  useEffect(() => {
    const element = containerRef.current;
    if (!element || mapRef.current) {
      return undefined;
    }

    const map = createMap(element, {
      center: DEFAULT_CENTER,
      zoom: EMPTY_ZOOM,
      zoomControl: true,
      attributionControl: true,
    });
    tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    map.on('click', (event: LeafletMouseEvent) => {
      if (disabledRef.current) {
        return;
      }
      const { lat, lng } = event.latlng;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }
      onChangeRef.current(Number(lat.toFixed(6)), Number(lng.toFixed(6)));
    });

    mapRef.current = map;

    const invalidate = () => {
      if (!mapRef.current || !hasMapSize(map)) {
        return;
      }
      map.invalidateSize({ animate: false });
    };

    requestAnimationFrame(invalidate);
    const timeout = window.setTimeout(invalidate, 200);
    const lateTimeout = window.setTimeout(invalidate, 520);
    window.addEventListener('resize', invalidate);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(invalidate);
    observer?.observe(element);

    return () => {
      window.clearTimeout(timeout);
      window.clearTimeout(lateTimeout);
      window.removeEventListener('resize', invalidate);
      observer?.disconnect();
      markerRef.current = null;
      circleRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    if (!isCoord(latitude) || !isCoord(longitude)) {
      dropLayer(map, markerRef.current);
      dropLayer(map, circleRef.current);
      markerRef.current = null;
      circleRef.current = null;
      map.setView(DEFAULT_CENTER, EMPTY_ZOOM, { animate: false });
      return;
    }

    const point = latLng(latitude, longitude);
    if (!markerRef.current) {
      markerRef.current = circleMarker(point, {
        radius: 8,
        color: '#1d4ed8',
        weight: 2,
        fillColor: '#2563eb',
        fillOpacity: 0.9,
      }).addTo(map);
    } else {
      markerRef.current.setLatLng(point);
    }

    const radius = radiusMeters && radiusMeters > 0 ? radiusMeters : 0;
    if (radius > 0) {
      if (!circleRef.current) {
        circleRef.current = circle(point, {
          radius,
          color: '#2563eb',
          weight: 1,
          fillColor: '#93c5fd',
          fillOpacity: 0.2,
        }).addTo(map);
      } else {
        circleRef.current.setLatLng(point);
        circleRef.current.setRadius(radius);
      }
    } else {
      dropLayer(map, circleRef.current);
      circleRef.current = null;
    }

    const currentZoom = map.getZoom();
    const nextZoom = Number.isFinite(currentZoom) && currentZoom >= DEFAULT_ZOOM ? currentZoom : DEFAULT_ZOOM;
    map.setView(point, nextZoom, { animate: false });
    if (hasMapSize(map)) {
      map.invalidateSize({ animate: false });
    }
  }, [latitude, longitude, radiusMeters]);

  const useBrowserLocation = () => {
    if (disabled || typeof navigator === 'undefined' || !navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onChange(
          Number(position.coords.latitude.toFixed(6)),
          Number(position.coords.longitude.toFixed(6)),
        );
      },
      () => undefined,
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 },
    );
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <span>{t('pickOnMap')}</span>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={useBrowserLocation}
          disabled={disabled}
        >
          {t('useMyLocation')}
        </button>
      </div>
      <p className={styles.hint}>{t('pickOnMapHint')}</p>
      <div
        ref={containerRef}
        className={`${styles.map} ${compact ? styles.mapCompact : ''}`}
        role="application"
        aria-label={t('pickOnMap')}
      />
    </div>
  );
};
